import { useList, useOne, useCreate, useGetIdentity } from "@pankod/refine-core";
import { Typography, Collapse, Button, Input, Form, notification, Popconfirm, PopconfirmProps, Descriptions } from "@pankod/refine-antd";
import { useState, useEffect } from "react";

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface assignedInvoice {
  id_invoice_type: string;
  id_user: string;
  description: string;
  username: string;
}

interface AssignInvoicesProps {
  eventId: string;
}

export const AssignInvoices: React.FC<AssignInvoicesProps> = ({ eventId }) => {
  const [pendingTypes, setPendingTypes] = useState<string[]>([]);
  const [assignedInvoices, setAssignedInvoices] = useState<assignedInvoice[]>([]);

  const { data: user } = useGetIdentity();

  const { data: event, isLoading: isEventLoading } = useOne({
    resource: "events",
    id: eventId,
  });

  const { data: invoiceTypes, isLoading: isInvoiceTypesLoading } = useList({
    resource: "invoice-types",
  });
 
  const { data: eventInvoiceTypes, refetch } = useOne({
    resource: "events/assign-invoices",
    id: eventId,
  });

  const { mutate: invoiceMutate } = useCreate();

  useEffect(() => {
    if (eventInvoiceTypes?.data) {
      const initialAssignedInvoices = eventInvoiceTypes?.data?.data?.map((assignedInvoice: any) => ({
        id: assignedInvoice.id,
        id_invoice_type: assignedInvoice.id_invoice_type,
        id_user: assignedInvoice.id_user,
        description: assignedInvoice.description,
        username: assignedInvoice.user?.name || assignedInvoice.id_user,
      }))
      setAssignedInvoices(initialAssignedInvoices);
    }
  }, [eventInvoiceTypes]);

  const handleSaveDescription = (id_invoice_type: string, description: string) => {
    setPendingTypes((prev) => [...prev, id_invoice_type]);
    const assignedInvoice = assignedInvoices.find((t) => t.id_invoice_type === id_invoice_type && t.id_user === user.id);
    invoiceMutate(
      {
        resource: "events/assign-invoices",
        values: {
          id_event: eventId,
          id_invoice_type,
          action: assignedInvoice ? "update" : "add",
          description,
        },
        successNotification: false,
      },
      {
        onSuccess: () => {
          notification.success({
            message: "Factura asignada",
            description: "La factura se guardó correctamente.",
          });
          setPendingTypes((prev) => prev.filter((tid) => tid !== id_invoice_type));
          refetch();
        },
        onError: (error) => {
          notification.error({
            message: "Error",
            description: error?.data?.message || "Ocurrió un error al guardar la descripción.",
          });
          setPendingTypes((prev) => prev.filter((tid) => tid !== id_invoice_type));
        },
      }
    );
  };

  const handleInvoiceDelete = (typeId: any) => {
    invoiceMutate(
      {
        resource: "events/assign-invoices",
        values: {
          id_event: eventId,
          id_invoice_type: typeId,
          action: 'delete'
        },
        successNotification: false,
      },
      {
        onSuccess: (response: any) => {
          if (response?.data?.success) {
            notification.success({
              message: "Factura eliminada",
              description: "Se ha eliminado correctamente la asignación de factura.",
            });
            setPendingTypes((prev) => prev.filter((tid) => tid !== typeId));
          } else {
            notification.error({
              message: "Error",
              description: response?.data?.message || "Ocurrió un error al eliminar la factura",
            });
            setPendingTypes((prev) => prev.filter((tid) => tid !== typeId));
          }
          refetch();
        },
        onError: (error) => {
          notification.error({
            message: "Error",
            description: error?.data?.message || "Ocurrió un error al guardar la descripción.",
          });
          setPendingTypes((prev) => prev.filter((tid) => tid !== typeId));
        },
      }
    );
  };

  if (isInvoiceTypesLoading || isEventLoading || !eventInvoiceTypes) {
    return <Text>Cargando datos del evento...</Text>;
  }

  return (
    <div style={{ padding: "20px" }}>
      <Title level={3}>{event?.data.name}</Title>
      <div>
        <Text strong>Fecha:</Text> <Text>{event?.data.event_date}</Text>
      </div>
      <div>
        <Text strong>Hora:</Text> <Text>{event?.data.start_time} - {event?.data.end_time}</Text>
      </div>
      { assignedInvoices && assignedInvoices.length > 0 && (
        <>
          <Title level={4} style={{ marginTop: "20px" }}>
            Facturas Asignadas:
          </Title>

          { assignedInvoices.map( assignedInvoice => {
            const invoiceTypeName = invoiceTypes?.data.find((t) => t.id === assignedInvoice.id_invoice_type)?.name;
            return (
              <div key={`assigned-invoice-${assignedInvoice.id_invoice_type}-${assignedInvoice.id_user}`}>
                <Text strong>{`[${assignedInvoice.username}] (${invoiceTypeName})  :`}</Text>
                <Text>{assignedInvoice?.description}</Text>
              </div>
            );
          })}
        </>
      )}

      <Title level={4} style={{ marginTop: "20px" }}>
        Nueva Factura:
      </Title>
      
      <Collapse accordion>
      {invoiceTypes?.data.map((type: any) => {
        const isEditable = true;

        const assignedInvoice = assignedInvoices.find((t) => t.id_invoice_type === type.id && t.id_user === user.id);
        const userName = assignedInvoice?.username ? `[${assignedInvoice.username}] ` : "";

        return (
          <Panel
            header={
              <>
                {userName}
                {type.name}
              </>
            }
            key={`panel-${type.id}`}
          >
            { isEditable ? (
              <Form
                key={`form-${type.id}-${assignedInvoice?.id_invoice_type}`} // Forzar reinicio del formulario
                layout="vertical"
                onFinish={(values: any) => {
                  return handleSaveDescription(type.id, values.description)
                }}
                initialValues={{
                  description: assignedInvoice?.description,
                }}
              >
                <Form.Item
                  name={`description`}
                  label="Descripción de la factura"
                  rules={[{ required: true, message: "Por favor ingrese una descripción." }]}
                >
                  <Input.TextArea rows={3} disabled={!isEditable} />
                </Form.Item>
                {isEditable && (
                  <>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={pendingTypes.includes(type.id)}
                    >
                      {assignedInvoice?.description ? "Actualizar" : "Crear"}
                    </Button>
                    { assignedInvoice?.description[type.id] && (
                      <Popconfirm
                        title="¿Seguro desea eliminar esta factura?"
                        onConfirm={() => handleInvoiceDelete(type.id)}
                        okText="Sí"
                        cancelText="No"
                      >
                        <Button
                          type="primary"
                          loading={pendingTypes.includes(type.id)}
                          style={{ marginLeft: '10px' }}
                          danger={true}
                        >
                          Eliminar
                        </Button> 
                      </Popconfirm>
                    )}              
                  </>
                )}
              </Form>
            ) : (
              <div>
                <Text strong>Descripción:</Text> <Text>{assignedInvoice?.description}</Text>
              </div>
          )}
          </Panel>
        );
      })}
      </Collapse>
    </div>
  );
};
